import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import type { RootState } from '../index';
import {
    CPM_CATALOG_VIEW,
    CPM_MASTER_AREA,
    CPM_MASTER_PAGE_SIZE,
    masterListLevel,
} from '../../constants/centralProductMaster.constants';
import { listMasters } from '../../services/productMasterService';
import type {
    CpmCatalogView,
    CpmMasterArea,
    CpmMasterEditorFilters,
    CpmMasterListRequest,
    CpmMasterListTotals,
    CpmMasterRecord,
    CpmVariantNested,
} from '../../types/centralProductMaster.types';

export type CpmSelectableMaster = CpmMasterRecord | CpmVariantNested;

export interface CpmSelectedMasterEntry {
    record: CpmSelectableMaster;
    nested: boolean;
}

/** How the next list fetch should present in the UI (matches Master Editor design). */
export type MasterListIntent = 'replace' | 'page' | 'search';

function defaultFilters(): CpmMasterEditorFilters {
    return {
        query: '',
        status: '',
        brandId: null,
        brandLabel: null,
        state: null,
        exciseCategoryId: null,
        sortBy: 'name',
        sortDir: 'asc',
        page: 1,
        limit: CPM_MASTER_PAGE_SIZE,
    };
}

export function tableLabelForArea(area: CpmMasterArea): string {
    if (area === 'brands') return 'Loading brands';
    if (area === 'stateProducts') return 'Loading state products';
    return 'Loading products & variants';
}

export function tableLabelForView(view: CpmCatalogView): string {
    if (view === 'combined') return 'Loading products with variants';
    if (view === 'products') return 'Loading products only';
    return 'Loading variants only';
}

const SORT_COLUMN_LABEL: Record<string, string> = {
    name: 'name',
    status: 'status',
    createdAt: 'created',
    aliases: 'aliases',
    countryOfOrigin: 'country of origin',
    parentCompany: 'parent company',
    products: 'products',
    brand: 'brand',
    variants: 'variants',
    alcoholContent: 'alcohol content',
    product: 'product',
    volume: 'volume',
    container: 'container',
    itemPerCase: 'items / case',
    barcodes: 'barcodes',
    state: 'state',
    variant: 'variant',
    excise: 'excise category',
    subcategory: 'subcategory',
    license: 'license type',
    purchasePrice: 'purchase price',
    retailPrice: 'retail price',
    wholesalePrice: 'wholesale price',
    distilleryPrice: 'distillery price',
};

function sortBusyLabel(sortBy: string): string {
    const col = SORT_COLUMN_LABEL[sortBy] ?? sortBy;
    return `Sorting by ${col}`;
}

interface CpmMasterEditorState {
    area: CpmMasterArea;
    catalogView: CpmCatalogView;
    filters: CpmMasterEditorFilters;
    items: CpmMasterRecord[];
    pagination: { page: number; limit: number; total: number; totalPages: number } | null;
    loading: 'idle' | 'loading' | 'success' | 'error';
    error: string | null;
    /** replace = full skeleton; page = fade + footer; search = search-box spinner only. */
    listIntent: MasterListIntent;
    /** Skeleton header label when listIntent === 'replace'. */
    tableLabel: string;
    /** Footer busy label when listIntent === 'page'. */
    pageBusyLabel: string;
    searching: boolean;
    listFetchId: string | null;
    /** Catalog-wide counts for the active area only — comes with the list response. */
    totals: CpmMasterListTotals;
    selectedIds: string[];
    /** Full rows for selectedIds so merge / delete still work after search / page change. */
    selectedItemsById: Record<string, CpmSelectedMasterEntry>;
    collapsedIds: string[];
    bulkMode: boolean;
    bulkDrafts: Record<string, Record<string, unknown>>;
    /** Client-side sort for nested variants in combined view. */
    childSortKey: string;
    childSortDir: 'asc' | 'desc';
}

const initialState: CpmMasterEditorState = {
    area: CPM_MASTER_AREA.catalog,
    catalogView: CPM_CATALOG_VIEW.combined,
    filters: defaultFilters(),
    items: [],
    pagination: null,
    loading: 'idle',
    error: null,
    listIntent: 'replace',
    tableLabel: 'Loading the master catalog',
    pageBusyLabel: '',
    searching: false,
    listFetchId: null,
    totals: {},
    selectedIds: [],
    selectedItemsById: {},
    collapsedIds: [],
    bulkMode: false,
    bulkDrafts: {},
    childSortKey: '',
    childSortDir: 'asc',
};

function applyReplaceIntent(state: CpmMasterEditorState, label: string) {
    state.listIntent = 'replace';
    state.tableLabel = label;
    state.pageBusyLabel = '';
    state.searching = false;
    state.items = [];
    state.pagination = null;
}

function applyPageIntent(state: CpmMasterEditorState, label: string) {
    state.listIntent = 'page';
    state.pageBusyLabel = label;
    state.searching = false;
}

function applySearchIntent(state: CpmMasterEditorState) {
    state.listIntent = 'search';
    state.searching = true;
    state.pageBusyLabel = '';
}

function resetSelection(state: CpmMasterEditorState) {
    state.selectedIds = [];
    state.selectedItemsById = {};
}

function buildListBody(
    area: CpmMasterArea,
    catalogView: CpmCatalogView,
    filters: CpmMasterEditorFilters
): CpmMasterListRequest {
    const level = masterListLevel(area, catalogView);
    const body: CpmMasterListRequest = {
        level,
        page: filters.page,
        limit: filters.limit,
        sortBy: filters.sortBy,
        sortDir: filters.sortDir,
        // Header stat + active tab badge ride along with the page.
        withTotals: true,
    };
    if (filters.query.trim()) body.search = filters.query.trim();
    if (filters.status) body.status = filters.status;
    if (area === 'catalog' && filters.brandId) body.brandId = filters.brandId;
    if (area === 'stateProducts' && filters.state) body.state = filters.state;
    if (area === 'stateProducts' && filters.exciseCategoryId) {
        body.exciseCategoryId = filters.exciseCategoryId;
    }
    if (area === 'catalog' && catalogView === 'combined') body.withVariants = true;
    return body;
}

export const fetchMasterList = createAsyncThunk(
    'cpmMasterEditor/fetchList',
    async (_, { getState }) => {
        const state = (getState() as RootState).cpmMasterEditor;
        const data = await listMasters(buildListBody(state.area, state.catalogView, state.filters));
        return data;
    }
);

const cpmMasterEditorSlice = createSlice({
    name: 'cpmMasterEditor',
    initialState,
    reducers: {
        setArea(state, action: PayloadAction<CpmMasterArea>) {
            state.area = action.payload;
            state.filters.page = 1;
            resetSelection(state);
            state.bulkMode = false;
            state.bulkDrafts = {};
            state.childSortKey = '';
            state.childSortDir = 'asc';
            state.filters.sortBy = 'name';
            state.filters.sortDir = 'asc';
            if (action.payload !== 'catalog') {
                state.filters.brandId = null;
                state.filters.brandLabel = null;
            }
            if (action.payload !== 'stateProducts') {
                state.filters.state = null;
                state.filters.exciseCategoryId = null;
            }
            applyReplaceIntent(state, tableLabelForArea(action.payload));
        },
        setCatalogView(state, action: PayloadAction<CpmCatalogView>) {
            state.catalogView = action.payload;
            state.filters.page = 1;
            resetSelection(state);
            state.collapsedIds = [];
            state.bulkMode = false;
            state.bulkDrafts = {};
            state.childSortKey = '';
            state.childSortDir = 'asc';
            applyReplaceIntent(state, tableLabelForView(action.payload));
        },
        patchFilters(state, action: PayloadAction<Partial<CpmMasterEditorFilters>>) {
            const patch = action.payload;
            const keys = Object.keys(patch).filter((k) => k !== 'page');

            state.filters = {
                ...state.filters,
                ...patch,
                page: patch.page ?? 1,
            };

            // Infer list intent from what changed (design: search vs busyPage vs loadTable).
            if (keys.length === 1 && keys[0] === 'query') {
                applySearchIntent(state);
            } else if (keys.includes('limit')) {
                applyReplaceIntent(state, 'Reloading rows');
            } else if (keys.includes('sortBy') || keys.includes('sortDir')) {
                applyPageIntent(state, sortBusyLabel(patch.sortBy ?? state.filters.sortBy));
            } else if (keys.includes('status')) {
                applyPageIntent(state, 'Applying status filter');
            } else if (keys.includes('brandId') || keys.includes('brandLabel')) {
                applyPageIntent(state, 'Applying brand filter');
            } else if (keys.includes('state') || keys.includes('exciseCategoryId')) {
                applyPageIntent(state, 'Applying state filter');
            } else if (keys.length === 0 && patch.page != null) {
                applyPageIntent(state, 'Fetching page');
            } else {
                applyPageIntent(state, 'Fetching page');
            }
        },
        setChildSort(state, action: PayloadAction<{ key: string; dir?: 'asc' | 'desc' }>) {
            const key = action.payload.key;
            if (state.childSortKey === key) {
                state.childSortDir =
                    action.payload.dir ?? (state.childSortDir === 'asc' ? 'desc' : 'asc');
            } else {
                state.childSortKey = key;
                state.childSortDir = action.payload.dir ?? 'asc';
            }
        },
        setPage(state, action: PayloadAction<number>) {
            state.filters.page = action.payload;
            applyPageIntent(state, 'Fetching page');
        },
        clearFilters(state) {
            const limit = state.filters.limit;
            state.filters = { ...defaultFilters(), limit };
            resetSelection(state);
            applyPageIntent(state, 'Clearing filters');
        },
        /** Prepare a replace reload (e.g. error retry) before dispatching fetchMasterList. */
        beginListReload(
            state,
            action: PayloadAction<{ intent?: MasterListIntent; label: string } | undefined>
        ) {
            const intent = action.payload?.intent ?? 'replace';
            const label = action.payload?.label ?? 'Reloading records';
            if (intent === 'replace') applyReplaceIntent(state, label);
            else if (intent === 'search') applySearchIntent(state);
            else applyPageIntent(state, label);
        },
        toggleSelectedItem(
            state,
            action: PayloadAction<{ record: CpmSelectableMaster; nested?: boolean }>
        ) {
            const { record, nested = false } = action.payload;
            const id = record._id;
            if (state.selectedIds.includes(id)) {
                state.selectedIds = state.selectedIds.filter((x) => x !== id);
                delete state.selectedItemsById[id];
            } else {
                state.selectedIds.push(id);
                state.selectedItemsById[id] = { record, nested };
            }
        },
        setPageSelection(
            state,
            action: PayloadAction<{ records: CpmMasterRecord[]; checked: boolean }>
        ) {
            const { records, checked } = action.payload;
            if (checked) {
                for (const record of records) {
                    if (!state.selectedIds.includes(record._id)) state.selectedIds.push(record._id);
                    state.selectedItemsById[record._id] = { record, nested: false };
                }
                return;
            }
            const drop = new Set(records.map((r) => r._id));
            state.selectedIds = state.selectedIds.filter((id) => !drop.has(id));
            for (const id of drop) delete state.selectedItemsById[id];
        },
        clearSelection(state) {
            resetSelection(state);
        },
        toggleCollapsed(state, action: PayloadAction<string>) {
            const id = action.payload;
            if (state.collapsedIds.includes(id)) {
                state.collapsedIds = state.collapsedIds.filter((x) => x !== id);
            } else {
                state.collapsedIds.push(id);
            }
        },
        expandAllRows(state) {
            state.collapsedIds = [];
        },
        collapseAllRows(state, action: PayloadAction<string[]>) {
            state.collapsedIds = action.payload;
        },
        enterBulkMode(state) {
            state.bulkMode = true;
            resetSelection(state);
            state.bulkDrafts = {};
        },
        exitBulkMode(state) {
            state.bulkMode = false;
            state.bulkDrafts = {};
        },
        setBulkDraftField(
            state,
            action: PayloadAction<{
                key: string;
                field: string;
                value: unknown;
            }>
        ) {
            const { key, field, value } = action.payload;
            if (!state.bulkDrafts[key]) state.bulkDrafts[key] = {};
            state.bulkDrafts[key][field] = value;
        },
        clearBulkDrafts(state) {
            state.bulkDrafts = {};
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchMasterList.pending, (state, action) => {
                state.loading = 'loading';
                state.error = null;
                state.listFetchId = action.meta.requestId;
                if (state.listIntent === 'replace') {
                    state.items = [];
                    state.pagination = null;
                }
                if (state.listIntent === 'search') {
                    state.searching = true;
                }
            })
            .addCase(fetchMasterList.fulfilled, (state, action) => {
                if (state.listFetchId !== action.meta.requestId) return;
                state.loading = 'success';
                state.items = action.payload.items;
                state.pagination = action.payload.pagination;
                state.totals = action.payload.totals ?? {};
                state.searching = false;
                state.pageBusyLabel = '';
                state.error = null;
            })
            .addCase(fetchMasterList.rejected, (state, action) => {
                if (state.listFetchId !== action.meta.requestId) return;
                state.loading = 'error';
                state.error = action.error.message || 'Failed to load master catalog';
                state.searching = false;
                state.pageBusyLabel = '';
            });
    },
});

export const {
    setArea,
    setCatalogView,
    patchFilters,
    setPage,
    clearFilters,
    beginListReload,
    toggleSelectedItem,
    setPageSelection,
    clearSelection,
    toggleCollapsed,
    expandAllRows,
    collapseAllRows,
    enterBulkMode,
    exitBulkMode,
    setBulkDraftField,
    clearBulkDrafts,
    setChildSort,
} = cpmMasterEditorSlice.actions;

export const useCpmMasterEditor = () => useSelector((state: RootState) => state.cpmMasterEditor);

export default cpmMasterEditorSlice.reducer;
